import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  Chip,
  AppBar,
  Toolbar,
  IconButton,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  Stack,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Schedule as ScheduleIcon,
  Email as EmailIcon,
  Star as StarIcon,
  QrCode as QrCodeIcon,
  Language as LanguageIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Security as SecurityIcon,
  TrendingUp as TrendingUpIcon,
  AutoAwesome as AutoAwesomeIcon,
  Support as SupportIcon,
  Verified as VerifiedIcon,
  BusinessCenter as BusinessCenterIcon,
  AttachMoney as AttachMoneyIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const PricingPage = () => {
  const navigate = useNavigate();
  const [pricingTab, setPricingTab] = useState(1); // 0 = Mensuel, 1 = Annuel (par défaut)
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleSubscribe = () => {
    // Redirect to signup page with pricing plan info
    const planType = pricingTab === 0 ? 'monthly' : 'annual';
    navigate(`/auth?mode=signup&plan=${planType}`);
  };

  const premiumFeatures = [
    {
      icon: <ScheduleIcon sx={{ fontSize: 28 }} />,
      title: 'Planning de rendez-vous intelligent',
      description: 'Système de réservation en ligne automatisé avec gestion des créneaux et notifications',
      category: 'Gestion'
    },
    {
      icon: <EmailIcon sx={{ fontSize: 28 }} />,
      title: 'Marketing automation',
      description: 'Emails automatiques de rappel, confirmation et suivi client personnalisés',
      category: 'Marketing'
    },
    {
      icon: <StarIcon sx={{ fontSize: 28 }} />,
      title: 'Boost SEO & Réputation',
      description: 'Invitations automatiques aux avis Google pour améliorer votre visibilité',
      category: 'Visibilité'
    },
    {
      icon: <QrCodeIcon sx={{ fontSize: 28 }} />,
      title: 'QR Code professionnel',
      description: 'Code QR personnalisé pour votre établissement avec analytics intégrés',
      category: 'Digital'
    },
    {
      icon: <LanguageIcon sx={{ fontSize: 28 }} />,
      title: 'Site web professionnel',
      description: 'Fiche publique optimisée SEO avec présentation complète de vos services',
      category: 'Web'
    },
    {
      icon: <SearchIcon sx={{ fontSize: 28 }} />,
      title: 'Référencement premium',
      description: 'Présence prioritaire sur BookingBarber avec badge professionnel vérifié',
      category: 'Visibilité'
    }
  ];

  const benefits = [
    {
      icon: <AttachMoneyIcon sx={{ fontSize: 24, color: '#059669' }} />,
      title: 'Prix imbattables mondialement',
      description: 'Les tarifs les plus compétitifs du marché : 83% moins cher que la concurrence'
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 24, color: '#059669' }} />,
      title: 'Augmentez votre chiffre d\'affaires',
      description: 'Jusqu\'à 30% de réservations en plus grâce à notre système optimisé'
    },
    {
      icon: <PublicIcon sx={{ fontSize: 24, color: '#059669' }} />,
      title: 'Mission sociale',
      description: 'Notre objectif premier : aider les salons à prospérer, pas maximiser nos profits'
    }
  ];

  // Generate structured data for pricing page
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "BookingBarber - Plateforme de Réservation Beauté",
    "description": "Le service de réservation en ligne le moins cher au monde pour les professionnels de la beauté. Tarifs imbattables à partir de 25€/mois.",
    "brand": {
      "@type": "Brand",
      "name": "BookingBarber"
    },
    "offers": [
      {
        "@type": "Offer",
        "name": "Abonnement Mensuel",
        "price": "29.99",
        "priceCurrency": "EUR",
        "priceValidUntil": "2025-12-31",
        "availability": "https://schema.org/InStock",
        "url": "https://bookingbarber.com/pricing"
      },
      {
        "@type": "Offer",
        "name": "Abonnement Annuel",
        "price": "300",
        "priceCurrency": "EUR",
        "priceValidUntil": "2025-12-31",
        "availability": "https://schema.org/InStock",
        "url": "https://bookingbarber.com/pricing"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Tarifs BookingBarber - Le Service de Réservation Beauté le Moins Cher au Monde</title>
        <meta name="description" content="Découvrez nos tarifs imbattables ! BookingBarber est le service de réservation en ligne le moins cher au monde pour les professionnels de la beauté. À partir de 25€/mois seulement. Notre mission : aider les salons à prospérer avec des prix accessibles." />
        <meta name="keywords" content="tarifs bookingbarber, prix réservation beauté, service moins cher monde, abonnement salon beauté, plateforme réservation pas cher, bookingbarber prix, logiciel salon abordable" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://bookingbarber.com/pricing" />
        <meta property="og:title" content="Tarifs BookingBarber - Le Service de Réservation Beauté le Moins Cher au Monde" />
        <meta property="og:description" content="Découvrez nos tarifs imbattables ! À partir de 25€/mois seulement. Notre mission : aider les salons à prospérer." />
        <meta property="og:image" content="https://bookingbarber.com/Logos/logoFocus.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Tarifs BookingBarber - Le Service le Moins Cher au Monde" />
        <meta name="twitter:description" content="À partir de 25€/mois seulement. Notre mission : aider les salons à prospérer." />
        
        <link rel="canonical" href="https://bookingbarber.com/pricing" />
        
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <Box sx={{ minHeight: '100vh', backgroundColor: '#fafafa' }}>
      {/* Header */}
      <AppBar 
        position="static" 
        elevation={0}
        sx={{ 
          backgroundColor: 'white', 
          color: 'text.primary',
          borderBottom: '1px solid #e5e7eb'
        }}
      >
        <Toolbar>
          <IconButton 
            edge="start" 
            onClick={() => navigate('/')} 
            sx={{ 
              mr: 2,
              '&:hover': {
                backgroundColor: 'rgba(5, 150, 105, 0.1)'
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1, 
              fontWeight: 700,
              color: '#059669'
            }}
          >
            BookingBarber
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
        {/* Hero Section */}
        <Fade in timeout={800}>
          <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6, md: 6 } }}>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 800,
                color: 'text.primary',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '2.8rem' },
                mb: 3,
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                lineHeight: 1.2
              }}
            >
              Le Service le Moins Cher au Monde
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{
                mb: 2,
                maxWidth: { xs: '100%', sm: 800 },
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: { xs: '1.1rem', sm: '1.5rem', md: '1.3rem' },
                px: { xs: 2, sm: 0 }
              }}
            >
              Notre mission : aider les salons de beauté à prospérer avec des tarifs accessibles à tous
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                maxWidth: { xs: '100%', sm: 600 },
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: { xs: '1rem', sm: '1.2rem', md: '1.1rem' },
                px: { xs: 2, sm: 0 },
                fontWeight: 600,
                color: '#059669'
              }}
            >
              🌍 BookingBarber : Les prix les plus compétitifs du marché mondial
            </Typography>

            {/* Trial Banner */}
            <Card
              elevation={0}
              sx={{
                display: 'inline-block',
                background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                border: '2px solid #059669',
                borderRadius: 4,
                mb: 4
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
                  <AutoAwesomeIcon sx={{ fontSize: 32, color: '#059669' }} />
                  <Box textAlign="left">
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#047857' }}>
                      Offre de lancement
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#047857' }}>
                      15 jours d'essai gratuit • Sans carte bancaire
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Fade>

        {/* Pricing Section */}
        <Card sx={{ mb: { xs: 4, sm: 6, md: 6 }, overflow: 'visible' }}>
          <Box sx={{
            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
            color: 'white',
            p: { xs: 3, sm: 4, md: 3 },
            textAlign: 'center'
          }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '1.5rem', sm: '2rem', md: '1.8rem' }
              }}
            >
              Choisissez votre formule
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.9,
                fontWeight: 400,
                fontSize: { xs: '1rem', sm: '1.25rem', md: '1.1rem' }
              }}
            >
              Tarifs transparents, sans surprise
            </Typography>
          </Box>

          <CardContent sx={{ p: { xs: 2, sm: 4, md: 3 } }}>
            {/* Pricing Tabs */}
            <Box display="flex" justifyContent="center" mb={{ xs: 3, sm: 4, md: 3 }} px={{ xs: 1, sm: 0 }}>
              <Paper
                elevation={3}
                sx={{
                  borderRadius: 4,
                  p: 0.5,
                  backgroundColor: '#f8f9fa',
                  width: { xs: '100%', sm: 'auto' },
                  maxWidth: 320
                }}
              >
                <Tabs
                  value={pricingTab}
                  onChange={(e, newValue) => setPricingTab(newValue)}
                  variant={isMobile ? "fullWidth" : "standard"}
                  sx={{
                    '& .MuiTab-root': {
                      borderRadius: 3,
                      textTransform: 'none',
                      fontWeight: 600,
                      minWidth: { xs: 0, sm: 140 },
                      py: { xs: 1, sm: 1.5 },
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      flex: { xs: 1, sm: 'none' }
                    },
                    '& .Mui-selected': {
                      backgroundColor: 'white',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      color: 'primary.main'
                    },
                    '& .MuiTabs-indicator': {
                      display: 'none'
                    }
                  }}
                >
                  <Tab label="Mensuel" />
                  <Tab label="Annuel" />
                </Tabs>
              </Paper>
            </Box>

            {/* Pricing Card */}
            <Box
              display="flex"
              justifyContent="center"
              mb={{ xs: 3, sm: 4, md: 3 }}
              position="relative"
            >
              {pricingTab === 1 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 10,
                    width: 'auto'
                  }}
                >
                  <Chip
                    onClick={() => {}}
                    icon={<AutoAwesomeIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                    label={isMobile ? "RECOMMANDÉ" : "RECOMMANDÉ - ÉCONOMISEZ 96€"}
                    sx={{
                      backgroundColor: '#059669',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: { xs: '0.75rem', sm: '0.9rem' },
                      height: { xs: 28, sm: 36 },
                      '& .MuiChip-label': {
                        px: { xs: 1.5, sm: 2 }
                      }
                    }}
                  />
                </Box>
              )}
              
              <Card
                elevation={pricingTab === 1 ? 16 : 8}
                sx={{
                  maxWidth: { xs: '100%', sm: 500, md: 450 },
                  width: '100%',
                  border: pricingTab === 1 ? '3px solid #059669' : '2px solid #e5e7eb',
                  position: 'relative',
                  background: pricingTab === 1
                    ? 'linear-gradient(135deg, #ecfdf5 0%, #ffffff 100%)'
                    : 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
                  transform: { xs: 'scale(1)', sm: pricingTab === 1 ? 'scale(1.02)' : 'scale(1)' },
                  transition: 'all 0.3s ease',
                  mt: pricingTab === 1 ? { xs: 1.5, sm: 2 } : 0
                }}
              >
                <CardContent sx={{ p: { xs: 3, sm: 5, md: 4 }, textAlign: 'center' }}>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={{ xs: 1.5, sm: 2 }} mb={{ xs: 2, sm: 3 }}>
                    {pricingTab === 0 ? (
                      <BusinessCenterIcon sx={{ fontSize: { xs: 28, sm: 40 }, color: 'primary.main' }} />
                    ) : (
                      <TrendingUpIcon sx={{ fontSize: { xs: 28, sm: 40 }, color: '#059669' }} />
                    )}
                    <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2.25rem', md: '2rem' } }}>
                      {pricingTab === 0 ? 'Mensuel' : 'Annuel'}
                    </Typography>
                  </Box>
                  
                  <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.1rem' } }}>
                    {pricingTab === 0 ? 'Flexibilité maximale' : 'Meilleure valeur'}
                  </Typography>
                  
                  <Box my={{ xs: 3, sm: 4 }}>
                    <Typography
                      variant="h1"
                      sx={{
                        fontWeight: 800,
                        color: pricingTab === 1 ? '#059669' : '#1976d2',
                        fontSize: { xs: '2.5rem', sm: '3.5rem', md: '3rem' },
                        lineHeight: 1
                      }}
                    >
                      {pricingTab === 0 ? '29.99€' : '25€'}
                    </Typography>
                    <Typography variant="h5" color="text.secondary" sx={{ fontWeight: 500, fontSize: { xs: '1.1rem', sm: '1.5rem', md: '1.3rem' } }}>
                      par mois
                    </Typography>
                    
                    {pricingTab === 1 && (
                      <Box mt={{ xs: 2, sm: 3 }}>
                        <Typography variant="h6" sx={{ textDecoration: 'line-through', color: 'text.secondary', fontSize: { xs: '1rem', sm: '1.25rem', md: '1.1rem' } }}>
                          359.88€ en mensuel
                        </Typography>
                        <Typography variant="h5" sx={{ color: '#059669', fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.5rem', md: '1.3rem' } }}>
                          300€ facturés annuellement
                        </Typography>
                        <Chip
                          onClick={() => {}}
                          label="Économie de 59.88€"
                          sx={{
                            mt: 1.5,
                            fontWeight: 600,
                            backgroundColor: '#059669',
                            color: 'white',
                            fontSize: { xs: '0.8rem', sm: '0.9rem' }
                          }}
                        />
                      </Box>
                    )}
                  </Box>

                  {pricingTab === 1 && (
                    <Box mb={{ xs: 3, sm: 4 }}>
                      <Stack spacing={{ xs: 1.5, sm: 2 }}>
                        <Box display="flex" alignItems="center" justifyContent="center" gap={1.5}>
                          <CheckCircleIcon sx={{ color: '#059669', fontSize: { xs: 22, sm: 26 } }} />
                          <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1rem' } }}>
                            2 mois offerts
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" justifyContent="center" gap={1.5}>
                          <SecurityIcon sx={{ color: '#059669', fontSize: { xs: 22, sm: 26 } }} />
                          <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1rem' } }}>
                            Tranquillité d'esprit pour 12 mois
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={handleSubscribe}
                    sx={{
                      py: { xs: 2, sm: 2.5 },
                      backgroundColor: pricingTab === 1 ? '#059669' : '#1976d2',
                      fontSize: { xs: '1rem', sm: '1.2rem', md: '1.1rem' },
                      fontWeight: 700,
                      borderRadius: 3,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                      '&:hover': {
                        backgroundColor: pricingTab === 1 ? '#047857' : '#1565c0',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 32px rgba(0,0,0,0.3)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Commencer l'essai gratuit
                  </Button>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.9rem', md: '0.85rem' } }}>
                    <SecurityIcon sx={{ fontSize: { xs: 16, sm: 18 }, mr: 1 }} />
                    15 jours gratuits • Sans engagement • Sans carte bancaire
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </CardContent>
        </Card>

        {/* Features Section */}
        <Card sx={{ mb: { xs: 4, sm: 6, md: 6 } }}>
          <Box sx={{
            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
            color: 'white',
            p: { xs: 3, sm: 4, md: 3 },
            textAlign: 'center'
          }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.4rem' } }}>
              Fonctionnalités Premium Incluses
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, fontSize: { xs: '0.9rem', sm: '1rem', md: '0.95rem' } }}>
              Tous les outils dont vous avez besoin pour développer votre activité
            </Typography>
          </Box>

          <CardContent sx={{ p: { xs: 3, sm: 4, md: 3 } }}>
            <Grid container spacing={{ xs: 3, sm: 4 }}>
              {premiumFeatures.map((feature, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Card
                    elevation={2}
                    sx={{
                      height: '100%',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        elevation: 8,
                        transform: { xs: 'none', sm: 'translateY(-4px)' }
                      }
                    }}
                  >
                    <CardContent sx={{ p: { xs: 3, sm: 4, md: 3 } }}>
                      <Box display="flex" alignItems="flex-start" gap={{ xs: 2, sm: 3 }}>
                        <Box
                          sx={{
                            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                            color: 'white',
                            borderRadius: 3,
                            p: { xs: 1.5, sm: 2 },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: { xs: 52, sm: 68, md: 60 },
                            minHeight: { xs: 52, sm: 68, md: 60 }
                          }}
                        >
                          {React.cloneElement(feature.icon, {
                            sx: { fontSize: { xs: 22, sm: 28 } }
                          })}
                        </Box>
                        <Box flex={1}>
                          <Chip
                            onClick={() => {}}
                            label={feature.category}
                            size="small"
                            sx={{
                              mb: 1.5,
                              fontWeight: 600,
                              fontSize: { xs: '0.7rem', sm: '0.75rem' },
                              cursor: 'default'
                            }}
                            color="primary"
                            variant="outlined"
                          />
                          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem', md: '1.1rem' } }}>
                            {feature.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.87rem' } }}>
                            {feature.description}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* Support Section */}
        <Card sx={{
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white',
          mb: { xs: 4, sm: 5, md: 5 }
        }}>
          <CardContent sx={{ p: { xs: 4, sm: 6, md: 5 }, textAlign: 'center' }}>
            <SupportIcon sx={{ fontSize: { xs: 48, sm: 64, md: 56 }, mb: 2 }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.4rem' } }}>
              Support Premium Inclus
            </Typography>
            <Typography variant="body1" sx={{
              mb: 4,
              opacity: 0.9,
              maxWidth: 600,
              mx: 'auto',
              fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1rem' },
              px: { xs: 1, sm: 0 }
            }}>
              Notre équipe d'experts est disponible pour vous accompagner dans la configuration
              et l'optimisation de votre abonnement professionnel
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<SupportIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />}
              onClick={() => window.location.href = 'mailto:support@omnivers.fr?subject=Paiement- BookingBarber'}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255,255,255,0.3)',
                color: 'white',
                fontWeight: 600,
                fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1rem' },
                py: { xs: 1.5, sm: 2 },
                px: { xs: 3, sm: 4 },
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.3)',
                  transform: { xs: 'none', sm: 'translateY(-2px)' }
                }
              }}
            >
              Contacter le Support
            </Button>
          </CardContent>
        </Card>

        {/* Final CTA */}
        <Box textAlign="center">
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/auth?mode=signup')}
            sx={{
              py: { xs: 2, sm: 2.5 },
              px: { xs: 4, sm: 6 },
              fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.2rem' },
              fontWeight: 700,
              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              color: 'white',
              borderRadius: 4,
              boxShadow: '0 8px 32px rgba(5, 150, 105, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #047857 0%, #065f46 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 40px rgba(5, 150, 105, 0.4)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Démarrer mon essai gratuit maintenant
          </Button>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2, fontSize: { xs: '0.9rem', sm: '1rem', md: '0.95rem' } }}>
            15 jours gratuits • Sans engagement • Sans carte bancaire
          </Typography>
        </Box>
      </Container>
    </Box>
    </>
  );
};

export default PricingPage;